import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { User } from '@/types/user';
import LoadingStatus from '@/components/ui/LoadingStatus';
import DelayedLoading from '@/components/ui/DelayedLoading';
import { roleHomePath } from '@/routes/roleHome';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: readonly User['role'][];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <DelayedLoading loading><LoadingStatus variant="page" label="Loading your workspace" description="Checking your secure session..." /></DelayedLoading>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0 && user && !requiredRoles.includes(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  // Players can only access routes that explicitly include 'player' in requiredRoles
  const isPlayerPortal = location.pathname === '/player' || location.pathname.startsWith('/player/');
  if (requiredRoles.length === 0 && user?.role === 'player' && !isPlayerPortal && location.pathname !== '/profile') {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
